import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ApplicationItem {
  application_id: number
  scheduled_date: string
  start_time: string | null
  end_time: string | null
  status: number
  applied_at: string
}

export default function CounselCheck() {
  const navigate = useNavigate()

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)

  const [applications, setApplications] = useState<ApplicationItem[]>([])
  const [searched, setSearched] = useState(false)

  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')

  useEffect(() => {
    checkLoginAndFetch()
  }, [])

  const checkLoginAndFetch = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      if (token) {
        const memberRes = await fetch(getApiUrl('/marubit/api/member/me'), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (memberRes.ok) {
          setIsLoggedIn(true)
          await fetchMemberApplications(token)
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchMemberApplications = async (token: string) => {
    const res = await fetch(getApiUrl('/marubit/api/counseling/applications/me?limit=100'), {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (res.ok) {
      setApplications(await res.json())
    }
  }

  const fetchGuestApplications = async () => {
    if (!guestName.trim() || !guestPhone.trim()) {
      alert('이름과 전화번호를 입력해주세요.')
      return
    }
    setSearching(true)
    try {
      const res = await fetch(getApiUrl('/marubit/api/counseling/guest/applications/list'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_name: guestName, guest_phone: guestPhone }),
      })
      if (res.ok) {
        setApplications(await res.json())
        setSearched(true)
      }
    } finally {
      setSearching(false)
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })

  const formatTime = (t: string | null) => t ? t.slice(0, 5) : '-'

  const statusLabel = (status: number) => {
    if (status === 0) return '확인대기'
    if (status === 1) return '확인완료'
    if (status === 2) return '신청취소'
    return '-'
  }

  const statusColor = (status: number) => {
    if (status === 2) return 'text-red-400 bg-red-50'
    if (status === 1) return 'text-green-600 bg-green-50'
    return 'text-[#887267] bg-[#f5f2f0]'
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">상담 신청 확인</h1>
          <p className="text-[14px] text-gray-400">신청한 상담 내역을 확인할 수 있습니다.</p>
        </div>

        {/* 비회원 검색 폼 */}
        {!isLoggedIn && (
          <div className="bg-white rounded-2xl p-7 mb-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">
              신청 내역 조회
            </h2>
            <p className="text-[12px] text-gray-400 ml-[13px] mb-4">
              신청 시 입력한 이름과 전화번호를 입력해주세요.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="이름"
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchGuestApplications()}
                className="flex-1 px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
              />
              <input
                type="tel"
                placeholder="전화번호"
                value={guestPhone}
                onChange={e => setGuestPhone(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchGuestApplications()}
                className="flex-1 px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
              />
              <button
                onClick={fetchGuestApplications}
                disabled={searching}
                className="px-6 py-3 bg-[#887267] text-white text-[14px] font-bold rounded-[10px] hover:bg-[#6b5248] transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {searching ? '조회 중...' : '조회하기'}
              </button>
            </div>
            <div className="mt-3 text-center">
              <button
                onClick={() => navigate('/auth/login')}
                className="text-[13px] text-[#887267] underline"
              >
                회원이신가요? 로그인하기
              </button>
            </div>
          </div>
        )}

        {/* 신청 목록 */}
        {(isLoggedIn || searched) && (
          <div>
            <p className="text-[14px] text-gray-400 mb-4">총 {applications.length}건</p>

            {applications.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center text-gray-300 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                신청 내역이 없습니다.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {applications.map(app => (
                  <div
                    key={app.application_id}
                    onClick={() => navigate(`/counsel/check/${app.application_id}`, {
                      state: { guest_name: guestName, guest_phone: guestPhone }
                    })}
                    className="bg-white rounded-2xl p-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex items-center justify-between gap-4 cursor-pointer hover:shadow-[0_4px_20px_rgba(136,114,103,0.15)] transition-shadow"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-bold text-[#222] mb-1">
                        {formatDate(app.scheduled_date)} 상담
                      </p>
                      {app.start_time && app.end_time && (
                        <p className="text-[13px] text-[#887267] font-medium mb-1">
                          {formatTime(app.start_time)} ~ {formatTime(app.end_time)}
                        </p>
                      )}
                      <p className="text-[12px] text-gray-300">{formatDateTime(app.applied_at)} 신청</p>
                    </div>
                    <span className={`shrink-0 px-3 py-1 text-[12px] font-semibold rounded-full ${statusColor(app.status)}`}>
                      {statusLabel(app.status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}